/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-member-accessibility */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { Inject, Injectable } from '@angular/core';
import { XsI18nPipe } from '@nx-fe/pipes';
import { AppSettingsService, FeatureToggleService, hd, XSightHttpService } from '@nx-fe/services';
import { Observable} from 'rxjs';
import { RcContext } from '../../remote-control-ng/context/rcContext';
import { RemoteControlDeviceState } from './device-state-registry.service';

type IDeviceModel = hd.IDeviceModel;
type IUnmanagedDeviceModel = hd.IUnmanagedDeviceModel;
type IError = hd.IError;


class DeviceIdToDeviceModelMap {
    [deviceId: string]: IDeviceModel;
}

export interface IMcSendScript {
  $type: string;
  content: string;
  id: string;
  name: string;
}

@Injectable()
export class RcDeviceService {

    private deviceServiceUrl: string = this.appSettingsService.deviceServiceUrl;

    constructor(
        private resourceService: XSightHttpService,
        private appSettingsService: AppSettingsService,
        private saI18nPipe: XsI18nPipe,
        private featureToggleService: FeatureToggleService,
    ) {
        console.log('rc-device startup');
    }

    /** @see IDeviceService.getDevicePermissionsResource */
    public getDevicePermissions = (deviceId: string, mcConnectionId?: number): Observable<hd.IRemoteControlPermissionsModel> => {
        return this.resourceService.get<hd.IRemoteControlPermissionsModel>(this.deviceServiceUrl + `${deviceId}/permissions`);
    };

    public getSendScriptPermissionResource = (): Observable<hd.IMcPermission> => this.resourceService.get<hd.IMcPermission>(
            this.deviceServiceUrl + 'sendScriptPermission'
        );

    public getDevicePermissionCompatibilityResource = (deviceId: string, desiredAction: string, mcConnectionId?: number): Observable<hd.IMcDeviceActionCompatibilityReport> => {

        //var queryParams = new Param { DesiredAction: desiredAction, DeviceIds: [deviceId] }
        return this.resourceService.get<hd.IMcDeviceActionCompatibilityReport>(
            this.deviceServiceUrl + 'compatibility', { params: { DesiredAction: desiredAction, DeviceIds: [deviceId] } });
    };

    /** @see IDeviceService.isRemoteControlSessionActive */
    // TODO - remove when feature toggle 'RemoteControlMp4Streaming' is removed
    //*
    public isRemoteControlSessionActive = (): boolean => RcContext.instance.session.isConnected();
    //*/

    public getDeviceActionScript(mcConnectionId?: number): Observable<IMcSendScript[]>{
        return this.resourceService.get<IMcSendScript[]>(this.deviceServiceUrl + 'sendScripts');
    }

    public setKioskMode = (deviceId: string, kioskMode: boolean, deviceFamily: string): void => {
        let message = '';

        if (deviceFamily.indexOf('WindowsCE') >= 0) {
            message = '\\windows\\mckiosk.exe ' + (kioskMode ? '' : '-q');
        } else if (deviceFamily.indexOf('AndroidPlus') >= 0) {
            message = '_adminmode ' + (kioskMode ? 'off' : 'on');
        }

        this.sendDeviceAction(
            deviceId,
            this.createDeviceActionInfo('SendScript', message));
    };

    public setKioskModeByAction = (deviceId: string, kioskMode: boolean, deviceFamily: string, errorHandler: (e) => void = undefined): void => {

        const errorFn = (error) => {
            errorHandler(`Error sending the ${kioskMode ? 'Enable': 'Disable'} Kiosk Mode action`);
        };

        if (kioskMode)
        {
            return this.sendDeviceAction(
                deviceId,
                this.createDeviceActionInfo('EnableKioskMode', null),
                errorFn);
        } else {
            return this.sendDeviceAction(
                deviceId,
                this.createDeviceActionInfo('DisableKioskMode', null),
                errorFn);
        }
    };

    public setAdminMode = (deviceId: string, adminMode: boolean, deviceFamily: string, errorHandler: (e) => void = undefined): void => {

        const errorFn = (error) => {
            errorHandler(`Error sending the ${adminMode ? 'Enable': 'Disable'} Admin Mode action`);
        };

        if (adminMode)
        {
            this.sendDeviceAction(
                deviceId,
                this.createDeviceActionInfo('EnableAdminMode', null),
                errorFn);
        } else {
            this.sendDeviceAction(
                deviceId,
                this.createDeviceActionInfo('DisableAdminMode', null),
                errorFn);
        }
    };

    public sendDeviceScript = (deviceId: string, actionContent: string): void => {
        this.sendDeviceAction(
            deviceId,
            this.createDeviceActionInfo('SendScript', actionContent));
    };

    public setDebugLoggingMode = (deviceId: string, debugLoggingMode: boolean, deviceFamily: string): void => {
        let message = '';

        if (deviceFamily.indexOf('AndroidPlus') >= 0) {
            message = 'writeprivateprofstring Logging LogEnabled ' + (debugLoggingMode ? 1 : 0) + '\n__setadvsetting';
        }

        this.sendDeviceAction(
            deviceId,
            this.createDeviceActionInfo('SendScript', message));
    };

    public sendDeviceMessage = (deviceId: string, message: string): void => {
        this.sendDeviceAction(
            deviceId,
            this.createDeviceActionInfo('SendMessage', message));
    };

    private createDeviceActionInfo = (action: string, message: string, queueOffline: boolean = true): hd.IDeviceActionInfo => ({
            Action: action,
            Message: message,
            QueueForOfflineDelivery: queueOffline
        } as hd.IDeviceActionInfo);

    public sendDeviceAction = (deviceId: string, actionInfo: hd.IDeviceActionInfo, errorHandler: (e) => void = undefined): void => {
        const deviceActionInfo = {
            DeviceId: deviceId,
            ActionInfo: JSON.stringify(actionInfo)
        };

        this.resourceService.post(this.deviceServiceUrl + 'deviceAction', deviceActionInfo).subscribe(rcResponse=>{
            console.log(`send device action ${rcResponse}`);
        },error => {
            if (!!errorHandler) {
                errorHandler(error);
            }
        });

        // resource.save(deviceActionInfo, (rcResponse) => {
        //     console.log(rcResponse);
        // });
    };

    /** @see IDeviceService.remoteControlDevice */
    // TODO - remove when feature toggle 'RemoteControlMp4Streaming' is removed
    //*
    public remoteControlDevice = (device: IDeviceModel, ticketId: string, startPoppedOut: boolean = false): Promise<string> => {
        let resource;
        if (device.mcConnectionId) {
            resource = this.resourceService.get(this.deviceServiceUrl + 'rc?mcConnectionId=' + device.mcConnectionId + '&mcDeviceId=' + device.mcDeviceId);
        } else {
            resource = this.resourceService.get(this.deviceServiceUrl + device.deviceId + '/rc');
        }

        return new Promise<string>((resolve, reject) => resource.subscribe((rcResponse: hd.IRemoteControlModel) => {
                if (rcResponse.data.ErrorCode || rcResponse.data.RemoteControlSecurityToken === undefined) {
                    // Either got an error code or token was not received due to error.
                    if (rcResponse.data.Message === undefined) {
                        rcResponse.data.Message = this.saI18nPipe.transform('remoteControl_UnknownErrorOccurred');
                    }
                    return reject({ error_description: rcResponse.data.Message } as IError);
                } else {
                    rcResponse.data['SotiAssistDeviceId'] = device.deviceId;
                    rcResponse.data['DeviceKind'] = device.kind;
                    RcContext.instance.featureToggleService = this.featureToggleService;
                    RcContext.instance.setResponseData(rcResponse.data);
                    RcContext.instance.install({
                        provider: null, // use default
                        host: rcResponse.uri
                    }, startPoppedOut);

                    return resolve(device.deviceId);
                }
            }, (err: IError) => {
                err.isHandled = true;
                return reject(err);
            }));
    };
    //*/

    // eslint-disable-next-line @typescript-eslint/explicit-member-accessibility
    public getRemoteControlData = (device: IDeviceModel): Promise<hd.IRemoteControlModel> => new Promise((resolve, reject)=>{
        let resource: Observable<any>;

        if (device.mcConnectionId) {
            // eslint-disable-next-line max-len
            resource = this.resourceService.get(`${this.deviceServiceUrl}rc?mcConnectionId=${device.mcConnectionId}&mcDeviceId=${device.mcDeviceId}`);
        } else {
            resource = this.resourceService.get(this.deviceServiceUrl + device.deviceId + '/rc');
        }
        resource.subscribe(rcResponse=>{
            rcResponse.data['SotiAssistDeviceId'] = device.deviceId;
            rcResponse.data['DeviceKind'] = device.kind;
            resolve(rcResponse);
        },(err=>{
            reject(err);
        }));

    });

    /**
     * Returns the _single_ css class representing the icon for the given device family
     *
     * @param deviceFamily The device family (or platform)
     */
    public getPlatformCssClass = (deviceFamily: string): string => {
        let platformCssClass = 'hd-icon-unknown';

        switch (deviceFamily) {
            case 'WindowsCE':
                platformCssClass = 'hd-icon-winclassic color-one';
                break;
            case 'WindowsPhone':
                platformCssClass = 'hd-icon-winmodern';
                break;
            case 'WindowsRuntime':
            case 'WindowsDesktop':
                platformCssClass = 'hd-icon-winclassic color-two';
                break;
            case 'Apple':
                platformCssClass = 'hd-icon-apple';
                break;
            case 'Android':
                platformCssClass = 'hd-icon-android';
                break;
            case 'AndroidPlus':
                platformCssClass = 'hd-icon-androidplus';
                break;
            /* Scanner and Printers have same Icons (hd-icon-printer), however there is no specific icon for Scanners */
            case 'Scanner':
                platformCssClass = 'hd-icon-printer';
                break;
            /* Printers are of type ZebraPrinters for which we have hd-icon-zebra icon class*/
            case 'Printer':
                platformCssClass = 'hd-icon-zebra';
                break;
            case 'Linux':
                platformCssClass = 'hd-icon-type-Linux';
                break;
                // Reusing Linux graphics temporarily.
            case 'Tizen':
                    platformCssClass = 'hd-icon-type-Linux';
                    break;
            case 'Unmanaged':
                platformCssClass = 'hd-icon-alldevice-alt';
                break;
        }
        return platformCssClass;
    };

    /**
     * Returns the _single_ css class representing the icon for the given device
     *
     * @param icon The device css class string
     * @param size Size of the icon, if not specified , it's icon-xxxlg
     */
    public generateDeviceIconString = (icon: string, size?: string): string => {
        let result = '';
        const iconSize = size || 'icon-xxxlg';
        if (icon !== '') {
            result = 'hd-icon middle overlap ' + icon + ' ' + iconSize;
        }
        return result;
    };

    /**
     * Returns the _single_ css class representing the icon for the given device
     *
     * @param deviceFamily The device family (or platform)
     */
    public getDeviceIconClass = (deviceFamily: string, size?: string): string => this.generateDeviceIconString(this.getDeviceCssClass(deviceFamily, ''), size);

    /**
     * Returns the _single_ css class representing the icon for the given device
     *
     * @param deviceFamily The device family (or platform)
     */
    public getDeviceScreenIconClass = (deviceFamily: string, size?: string): string => {
        const deviceScreenIconClass = this.generateDeviceIconString(this.getDeviceCssClass(deviceFamily, 'screen'), size);
        return deviceScreenIconClass;
    };

    /**
     * Returns the _single_ css class representing the offline or online icon for the given device
     *
     * @param deviceName The device name
     * @param isAgentOnline true if the agent is online
     */
    public getOnlineOfflineSuffix = (deviceName: string, isAgentOnline: boolean): string => {
        if (!deviceName || deviceName.length === 0) {
            return ' color-gray-lighter';
        } else {
            return isAgentOnline ? ' online' : ' offline';
        }
    };

    /**
     * Returns the _single_ css class representing the icon for the given device
     *
     * @param deviceFamily The device family (or platform)
     */
    public getDeviceCssClass = (deviceFamily: string, suffix: string): string => {
        let deviceIconClass = '';
        if (suffix === '') {
            deviceIconClass = 'hd-icon-question unknown-device-icon';
        }

        switch (deviceFamily) {
            case 'Android':
            case 'AndroidPlus':
            case 'WindowsPhone':
            case 'Apple':
                deviceIconClass = 'hd-icon-phone' + suffix;
                break;
            case 'AppleDesktop':
                deviceIconClass = 'hd-icon-desktop' + suffix;
                break;
            case 'WindowsRuntime':
            case 'WindowsDesktop':
                deviceIconClass = 'hd-icon-desktop' + suffix;
                break;
            case 'WindowsCE':
                deviceIconClass = 'hd-icon-' + suffix + (suffix !== '' ? '-' : '') + 'DeviceWindowsCE';
                break;
            case 'Scanner':
            case 'Printer':
                deviceIconClass = 'hd-icon-printer' + suffix;
                break;
            case 'Linux':
                deviceIconClass = 'hd-icon-DeviceLinux' + suffix;
                break;
            // Reusing Linux graphics temporarily.

            case 'Tizen':
                    deviceIconClass = 'hd-icon-DeviceLinux' + suffix;
                    break;
            case 'Unmanaged':
                deviceIconClass = 'hd-icon-alldevice-alt' + suffix;
                break;
        }

        return deviceIconClass;
    };

    /**
     * Returns the full CSS selector classes for the given device family.
     *
     * @param deviceFamily Device family to lookup CSS class for.
     */
    public getPlatformIconClass = (deviceFamily: string): string => {
        const platformIconClass = this.generatePlatformIconString(this.getPlatformCssClass(deviceFamily));
        return platformIconClass;
    };

    public getUnmanagedDevices = (): Observable<IUnmanagedDeviceModel> => this.resourceService.get<IUnmanagedDeviceModel>(
            this.deviceServiceUrl + 'unmanaged', { params: { unmanagedDeviceName: '@deviceName' } }
        );

    /**
     * Return the Device Family
     *
     * @param deviceKind The Device Kind
     * @param deviceFamily The Device Family
     * */
    public getDeviceFamily = (deviceKind: string, deviceFamily: string): string => {
        if (deviceKind === 'WindowsDesktop' && deviceFamily === 'WindowsPhone') {
            deviceFamily = 'WindowsDesktop';
        }
        if (deviceKind === 'Mac') {
            deviceFamily = 'AppleDesktop';
        }

        return deviceFamily ? deviceFamily : '';
    };

    /**
     * TODO: There are several device family check around remote control code. However, they are not exactly same.
     * Needs more investigation on whether those methods can be merged and put into one service.
     */

    public isAndroidDevice = (deviceState: RemoteControlDeviceState): boolean => {
        if (
            (deviceState.device.family.indexOf('AndroidPlus') >= 0 && deviceState.device.kind === 'AndroidPlus') ||
            (deviceState.device.family.indexOf('AndroidPlus') >= 0 &&
              deviceState.device.kind === 'AndroidForWork') ||
            (deviceState.device.family.indexOf('AndroidPlus') >= 0 && deviceState.device.kind === 'AndroidElm')
        ) {
            return true;
        } else {
            return false;
        }
    };

    public isWindowsDevice = (deviceState: RemoteControlDeviceState): boolean => {
      return deviceState?.device?.family.indexOf('WindowsCE') >= 0;
    };

    public isSpecificDeviceFamily = (deviceState: RemoteControlDeviceState, deviceFamily: string[]): boolean => {
        return !!deviceState?.device?.family && deviceFamily.includes(deviceState.device.family);
    };

    private generatePlatformIconString = (icon: string): string => {
        const result = 'hd-icon icon-sm ' + icon;
        return result;
    };
}
